import App from "App";
import Crumb from "features/breadcrumbs/Crumb";
import AuthRoute from "features/auth/AuthRoute";
import { UserRole } from "features/auth/types/user";
import UnAuthenticatedRoute from "features/auth/UnAuthenticatedRoute";
import RootRedirects from "features/RootRedirects";
import PendingVerificationRoute from "features/routes/PendingVerificationRoute";
import CompaniesPage from "pages/companies/CompaniesPage";
import Dashboard from "pages/dashboard/Dashboard";
import InstallerStatistics from "pages/installerStatistics/InstallerStatistics";
import Login from "pages/login/Login";
import ChecklistCrumb from "pages/orders/ChecklistCrumb";
import OrderCrumb from "pages/order/OrderCrumb";
import Orders from "pages/orders/OrdersPage";
import OrdersCrumb from "pages/orders/OrdersCrumb";
import ChecklistDetails from "pages/checklist/components/ChecklistDetails";
import InstallProductPage from "pages/install-product/InstallProductPage";
import OrderRowPage from "pages/order-row/OrderRowPage";
import ChecklistPage from "pages/checklist/ChecklistPage";
import OrderPage from "pages/order/OrderPage";
import PendingApproval from "pages/pendingApproval/PendingApproval";
import Profile from "pages/profile/Profile";
import ResetPassword from "pages/resetPassword/ResetPassword";
import SignUp from "pages/signUp/SignUp";
import TimeManagement from "pages/timeManagement/TimeManagement";
import ShowUser from "pages/user/UserPage";
import UsersPage from "pages/users/UsersPage";
import VerifyEmail from "pages/verifyEmail/VerifyEmail";
import { createBrowserRouter } from "react-router-dom";
import InstallProductCrumb from "pages/install-product/InstallProductCrumb";
import OrderRowCrumb from "pages/order-row/OrderRowCrumb";
import UserCrumb from "pages/user/UserCrumb";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { Group, Store } from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import OrderDetails from "pages/order/components/OrderDetails";
import BookingsPage from "pages/bookings/BookingsPage";
import ErrorPage from "pages/error/ErrorPage";
import AbsencePage from "pages/absence/AbsencePage";
import AssignedOrdersPage from "pages/assigned-orders/AssignedOrdersPage";
import OrderReviewPage from "pages/order-review/OrderReviewPage";
import SettingsPage from "pages/settings/SettingsPage";

export type Handle = {
  crumb?: React.ReactNode;
};

export const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <App />,
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          element: <RootRedirects />,
        },
        {
          element: <UnAuthenticatedRoute />,
          children: [
            { path: "login", element: <Login /> },
            { path: "reset", element: <ResetPassword /> },
            { path: "signup", element: <SignUp /> },
          ],
        },
        {
          element: <PendingVerificationRoute />,
          children: [
            { path: "verify", element: <VerifyEmail /> },
            { path: "pendingapproval", element: <PendingApproval /> },
          ],
        },
        {
          // Any authenticated user routes
          element: <AuthRoute />,
          children: [
            {
              path: "orders",
              handle: { crumb: <OrdersCrumb /> },
              children: [
                // Only employees can access order search
                {
                  element: (
                    <AuthRoute
                      roles={[
                        UserRole.ADMIN,
                        UserRole.USER_ADMIN,
                        UserRole.PAYROLL,
                        UserRole.EMPLOYEE,
                        UserRole.SALES,
                        UserRole.INTERNAL_INSTALLER,
                      ]}
                    />
                  ),
                  children: [
                    {
                      index: true,
                      element: <Orders />,
                    },
                  ],
                },
                // Access to order details is handled per order
                {
                  path: "view/:orderId",
                  element: <OrderPage />,
                  handle: { crumb: <OrderCrumb /> },
                  children: [
                    {
                      element: <OrderDetails />,
                      index: true,
                    },
                    {
                      path: "checklist/:checklistId",
                      element: <ChecklistPage />,
                      handle: { crumb: <ChecklistCrumb /> },
                      children: [
                        {
                          index: true,
                          element: <ChecklistDetails />,
                        },
                        {
                          path: "products",
                          handle: { crumb: <InstallProductCrumb /> },
                          children: [
                            {
                              index: true,
                              element: <InstallProductPage />,
                            },
                            {
                              path: ":rowId",
                              element: <OrderRowPage />,
                              handle: { crumb: <OrderRowCrumb /> },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: "profile",
              element: <Profile />,
              handle: {
                crumb: (
                  <Crumb icon={<AccountCircleIcon />} to="/profile">
                    Profil
                  </Crumb>
                ),
              },
            },
            // External installer routes
            {
              element: <AuthRoute roles={[UserRole.EXTERNAL_INSTALLER]} />,
              children: [{ path: "assigned-orders", element: <AssignedOrdersPage /> }],
            },
            // All employee routes
            {
              element: (
                <AuthRoute
                  roles={[
                    UserRole.ADMIN,
                    UserRole.USER_ADMIN,
                    UserRole.PAYROLL,
                    UserRole.EMPLOYEE,
                    UserRole.SALES,
                    UserRole.INTERNAL_INSTALLER,
                  ]}
                />
              ),
              children: [
                {
                  path: "dashboard",
                  element: <Dashboard />,
                  handle: {
                    crumb: (
                      <Crumb icon={<DashboardIcon />} to="/dashboard">
                        Dashboard
                      </Crumb>
                    ),
                  },
                },
                {
                  path: "absence",
                  element: <AbsencePage />,
                },
                {
                  path: "companies",
                  handle: {
                    crumb: (
                      <Crumb to="/companies" icon={<Store />}>
                        Företag
                      </Crumb>
                    ),
                  },
                  children: [
                    {
                      index: true,
                      element: <CompaniesPage />,
                    },
                  ],
                },
                // Internal installer routes
                {
                  path: "bookings",
                  element: <BookingsPage />,
                },
                // Payroll routes
                {
                  element: <AuthRoute roles={[UserRole.ADMIN, UserRole.PAYROLL]} />,
                  children: [{ path: "reports", element: <TimeManagement /> }],
                },
                // Useradmin routes
                {
                  element: <AuthRoute roles={[UserRole.ADMIN, UserRole.USER_ADMIN]} />,
                  children: [
                    {
                      path: "users",
                      handle: {
                        crumb: (
                          <Crumb to="/users" icon={<Group />}>
                            Användare
                          </Crumb>
                        ),
                      },
                      children: [
                        {
                          index: true,
                          element: <UsersPage />,
                        },
                        {
                          path: "view/:userId",
                          element: <ShowUser />,
                          handle: { crumb: <UserCrumb /> },
                        },
                      ],
                    },
                    {
                      path: "installer-statistics",
                      element: <InstallerStatistics name="Statistik" />,
                      handle: {
                        crumb: (
                          <Crumb to="/installer-statistics" icon={<Store />}>
                            Statistik
                          </Crumb>
                        ),
                      },
                    },
                    {
                      path: "order-review",
                      element: <OrderReviewPage />,
                    },
                  ],
                },
                // Admin routes
                {
                  element: <AuthRoute roles={[UserRole.ADMIN]} />,
                  children: [
                    {
                      path: "settings",
                      children: [
                        {
                          index: true,
                          element: <SettingsPage />,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "*",
      element: <ErrorPage.NotFound />,
    },
  ],
  {}
);
